'use client';

import { PropsWithChildren, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { AlertCode } from 'shared/types/checkout';
import { authenticateUser, getUserAccountProfile } from 'app/actions';
import { getEnvOrFail } from 'env/env';
import { getAccountFullName } from 'helpers/utils/account';
import {
  getAccountId,
  getIsAuthenticated,
  setAccount,
  setAccountId,
  setAlert,
  setIsAuthenticated,
  setUserAccountProfile,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { makeSDKCallWithRetries } from 'utils';
import { getLoginRedirectUrl, isCookieValid } from 'utils/edx-auth';
import { AuthenticationProviderProps } from './types';

const PUBLIC_ROUTES = ['redeem-discount/'];

export const AuthenticationProvider = ({
  children,
  locale,
}: PropsWithChildren<AuthenticationProviderProps>) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const router = useRouter();
  const pathname = usePathname();
  const customerId = useAppSelector(getAccountId);

  const isPublic = PUBLIC_ROUTES.some((route) => pathname.endsWith(route));

  useEffect(() => {
    const establishFrontasticSession = async () => {
      if (isPublic) {
        return;
      }
      // only do login refresh if there is less than 30 mins to expiration
      if (!isCookieValid(30 * 60)) {
        const loginRefreshEndpoint = getEnvOrFail(
          'NEXT_PUBLIC_EDX_LOGIN_REFRESH_ENDPOINT',
        );
        const loginRefreshResponse = await fetch(loginRefreshEndpoint, {
          method: 'POST',
          credentials: 'include',
        });

        if (!loginRefreshResponse.ok) {
          router.replace(getLoginRedirectUrl());
          return;
        }
      }
      const accountResult = await makeSDKCallWithRetries(
        'getAccount',
        customerId,
        () => authenticateUser(locale),
      );
      if (!accountResult || accountResult.isError) {
        console.error(
          `Error fetching account information with error message: ${
            accountResult.error.message ?? ''
          }, name: ${accountResult.error.name ?? ''}, stack: ${
            accountResult.error.stack ?? ''
          }`,
        );
        dispatch(
          setAlert({ code: AlertCode.FALLBACK_WITH_RELOAD_INFORMATION }),
        );
        return;
      }
      if (!accountResult.data.isError && accountResult.data.loggedIn) {
        dispatch(setIsAuthenticated(true));
        if (!customerId && accountResult.data.account?.accountId) {
          dispatch(setAccountId(accountResult.data.account.accountId));
        }
        // Store full account data in Redux for auto-filling checkout forms
        if (accountResult.data.account) {
          const fullName = getAccountFullName(accountResult.data.account).split(
            ' ',
          );

          // RV Policy splits "James L. Jones" as firstName: "James L." and lastName: "Jones",
          // where as edX puts only the first word in first name and all else in last.
          const lastName = fullName.length > 1 ? (fullName.pop() ?? '') : '';
          const firstName = fullName.join(' ');

          const reParsed = {
            ...accountResult.data.account,
            firstName,
            lastName,
          };

          dispatch(setAccount(reParsed));
        }

        // Fetch user account profile data from LMS and store in Redux
        if (
          accountResult.data.loggedIn &&
          accountResult.data.account?.edXUsername
        ) {
          try {
            const userAccountResult = await getUserAccountProfile(
              accountResult.data.account.edXUsername,
            );

            if (
              userAccountResult &&
              !userAccountResult.isError &&
              userAccountResult.data.userAccount
            ) {
              dispatch(
                setUserAccountProfile(userAccountResult.data.userAccount),
              );
            } else {
              console.warn('Failed to fetch user account profile data:', {
                result: userAccountResult,
                error: userAccountResult?.isError
                  ? userAccountResult.error
                  : null,
                isError: userAccountResult?.isError,
                hasData:
                  !userAccountResult?.isError && !!userAccountResult?.data,
                hasUserAccount:
                  !userAccountResult?.isError &&
                  !!userAccountResult?.data?.userAccount,
              });
            }
          } catch (error) {
            console.error('Error fetching user account profile:', error);
          }
        }
      } else {
        router.replace(getLoginRedirectUrl());
      }
    };

    establishFrontasticSession().catch((e: Error) => {
      console.error(
        `Error establishing Frontastic session with error message: ${
          e.message ?? ''
        }, name: ${e.name ?? ''}, stack: ${e.stack ?? ''}`,
      );
      dispatch(setAlert({ code: AlertCode.FALLBACK_WITH_RELOAD_INFORMATION }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  if (isPublic) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }
  if (isAuthenticated) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return null;
};
